import { useCallback, useEffect, useRef, useState } from 'react';
import { catalog, errorMessage, workplace } from '../../api/client';
import type { Assignment } from '../../api/types';
import { buildActivity, groupActivityByDay, refreshStamp, relativeTime, type ActivityAnnouncement, type ActivityContext, type ActivityItem } from '../../lib/activity';
import { Icon, EmptyState, Skeleton } from '../../components/ui';
import { ErrorNotice } from '../../components/common';
import { href } from '../../router';

/** How often the feed re-reads the workplace while the tab is in front. */
const REFRESH_MS = 60_000;

interface FeedState {
  items: ActivityItem[];
  loadedAt: number;
}

/**
 * Reads every workplace record the learner has — events, notifications, assignments and the
 * announcements for their department — and merges them into one feed. It refreshes itself every
 * minute while the tab is visible, and pauses when it is not, so nothing loads in the background.
 */
function useWorkplaceActivity(limit: number) {
  const [state, setState] = useState<FeedState>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const titles = useRef<Record<string, string>>();
  const context = useRef<{ ctx: ActivityContext; assignments: Assignment[]; announcements: ActivityAnnouncement[] }>();
  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      // The catalog and the seat rarely change, so they are read once per mount; the refresh
      // only re-reads the two feeds that actually move.
      if (!titles.current) {
        const { scenarios } = await catalog.scenarios();
        titles.current = Object.fromEntries(scenarios.map((s) => [s.id, s.title]));
      }
      if (!context.current) {
        const { workplace: w } = await workplace.overview();
        context.current = {
          ctx: { managerName: w.manager?.name, departmentName: w.department.name, scenarioTitles: titles.current },
          assignments: w.assignments,
          announcements: w.announcements,
        };
      }
      const held = context.current;
      const [{ events }, { notifications }] = await Promise.all([workplace.events(), workplace.notifications()]);
      if (!alive.current) return;
      const items = buildActivity({ events, notifications, assignments: held.assignments, announcements: held.announcements }, held.ctx, limit);
      setState({ items, loadedAt: Date.now() });
      setNow(Date.now());
      setError(undefined);
    } catch (e) {
      if (alive.current) setError(errorMessage(e));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    alive.current = true;
    void load();
    const tick = window.setInterval(() => {
      setNow(Date.now());
      if (document.visibilityState === 'visible') void load();
    }, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setNow(Date.now());
        void load();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive.current = false;
      window.clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  return { items: state?.items, loadedAt: state?.loadedAt, now, error, loading, reload: () => void load() };
}

export function ActivityFeed({ limit = 25, heading = 'Workplace activity', headingId = 'activity-title', level = 2, showEmptyAction = true }: { limit?: number; heading?: string; headingId?: string; level?: 2 | 3; showEmptyAction?: boolean }) {
  const { items, loadedAt, now, error, loading, reload } = useWorkplaceActivity(limit);
  const days = groupActivityByDay(items ?? [], new Date(now));
  const Heading = level === 2 ? 'h2' : 'h3';

  return (
    <section className="card" aria-labelledby={headingId}>
      <div className="card-head">
        <span className="icon-chip cyan" aria-hidden="true"><Icon name="clock" size={18} /></span>
        <Heading id={headingId}>{heading}</Heading>
        <button type="button" className="quiet small" onClick={reload} aria-label="Refresh workplace activity">Refresh</button>
      </div>
      <p className="feed-stamp" role="status" aria-live="polite">
        <span className="status-dot ok" aria-hidden="true" />
        {loadedAt ? refreshStamp(loadedAt, now) : 'Loading activity'} · checks every minute
      </p>
      <ErrorNotice message={error} onRetry={reload} />
      {loading && !items && (
        <div className="stack" style={{ marginTop: 'var(--sp-4)' }} aria-hidden="true">
          <Skeleton kind="text" width="40%" />
          <Skeleton kind="text" count={3} />
        </div>
      )}
      {items && items.length === 0 && !error && (
        <EmptyState icon="clock" title="Nothing has happened yet" actions={showEmptyAction ? <a className="button small" href={href({ page: 'assignments' })}>Ask for an assignment</a> : undefined}>
          Your workplace records every assignment, scenario and message here. Start something and it will show up.
        </EmptyState>
      )}
      {days.length > 0 && (
        <div className="activity-feed" style={{ marginTop: 'var(--sp-4)' }}>
          {days.map((day, di) => (
            <div className="feed-day reveal" style={{ ['--i' as string]: di }} key={day.key}>
              <p className="day-label">{day.label}</p>
              <ol>
                {day.items.map((item) => (
                  <li key={item.id}>
                    <span className={`dot ${item.tone}`} aria-hidden="true"><Icon name={item.icon} size={15} /></span>
                    <span className="what">
                      <strong>{item.href ? <a href={item.href}>{item.title}</a> : item.title}</strong>
                      {item.detail && <span className="detail">{item.detail}</span>}
                      <span className="when">
                        <time dateTime={item.at}>{relativeTime(item.at, new Date(now))}</time>
                        {item.unread && <> · <span className="badge accent">unread</span></>}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
      <p className="xs muted" style={{ marginTop: 'var(--sp-3)' }}>Every line is a record from your simulated workplace at TechCore Solutions. Nothing here is generated.</p>
    </section>
  );
}
